import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { photoUrl, BACKEND_URL } from "@/lib/api";
import Navbar from "@/components/app/Navbar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Send } from "lucide-react";

const POLL_INTERVAL_MS = 4000;

export default function Chat() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const scroller = useRef(null);

  useEffect(() => {
    const loadThreads = async () => {
      try {
        const r = await api.get("/threads");
        setThreads(r.data.threads);
        if (!threadId && r.data.threads[0]) setActive(r.data.threads[0]);
      } catch (e) {
        console.error("Failed to load threads", e);
      }
    };
    loadThreads();
  }, [threadId]);
  useEffect(() => {
    if (threadId && threads.length) {
      const t = threads.find((x) => x.id === threadId);
      if (t) setActive(t);
    }
  }, [threadId, threads]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get(`/threads/${active.id}/messages`);
        if (!cancelled) setMessages(r.data.messages);
      } catch (e) {
        console.error("Failed to load messages", e);
      }
    };
    load();
    const poll = setInterval(load, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(poll); };
  }, [active]);

  useEffect(() => {
    if (!user || !active) return;
    let ws;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/auth/ws-token");
        if (cancelled) return;
        const wsUrl = BACKEND_URL.replace(/^http/, "ws") + "/api/ws?token=" + encodeURIComponent(r.data.token);
        ws = new WebSocket(wsUrl);
        ws.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (data.kind === "message" && data.data.thread_id === active.id) {
              setMessages((m) => (m.some((x) => x.id === data.data.id) ? m : [...m, data.data]));
            }
          } catch (err) {
            console.error("WS parse error", err);
          }
        };
        ws.onerror = (err) => console.warn("WS error (polling will cover)", err.type);
      } catch (e) {
        console.warn("WS token fetch failed; using polling only", e?.response?.status);
      }
    })();
    return () => { cancelled = true; if (ws) ws.close(); };
  }, [user, active]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      const r = await api.post(`/threads/${active.id}/messages`, { text });
      setMessages((m) => (m.some((x) => x.id === r.data.message.id) ? m : [...m, r.data.message]));
      setText("");
    } catch (e) {
      console.error("Send failed", e);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl md:text-4xl font-medium mb-6">Chat</h1>
        <div className="grid md:grid-cols-12 gap-4 h-[70vh]">
          <aside className="md:col-span-4 bg-white border border-stone-200 rounded-2xl overflow-hidden flex flex-col">
            <div className="p-4 border-b border-stone-200 font-medium">Conversations</div>
            <div className="flex-1 overflow-y-auto">
              {threads.length === 0 && <div className="p-6 text-sm text-stone-500">No chats yet. Accepted enquiries open here.</div>}
              {threads.map((t) => (
                <button key={t.id} data-testid={`thread-${t.id}`}
                  onClick={() => { setActive(t); nav(`/chat/${t.id}`); }}
                  className={`w-full text-left p-4 border-b border-stone-100 hover:bg-stone-50 ${active?.id === t.id ? "bg-stone-50" : ""}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-stone-100 overflow-hidden shrink-0">
                      {t.listing?.photos?.[0] && <img src={photoUrl(t.listing.photos[0])} className="w-full h-full object-cover" alt="" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{t.other_user?.name || "User"}</div>
                      <div className="text-xs text-stone-500 truncate">{t.listing?.title || ""}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          <section className="md:col-span-8 bg-white border border-stone-200 rounded-2xl flex flex-col overflow-hidden">
            {!active ? (
              <div className="flex-1 grid place-items-center text-stone-500">Select a conversation</div>
            ) : (
              <>
                <div className="p-4 border-b border-stone-200 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-stone-100 overflow-hidden">
                    {active.listing?.photos?.[0] && <img src={photoUrl(active.listing.photos[0])} className="w-full h-full object-cover" alt="" />}
                  </div>
                  <div>
                    <div className="font-medium text-sm">{active.other_user?.name}</div>
                    <div className="text-xs text-stone-500">re: {active.listing?.title}</div>
                  </div>
                  <div className="ml-auto chip chip-verified">Masked chat</div>
                </div>
                <div ref={scroller} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FDFBF7]">
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}>
                      <div data-testid={`msg-${m.id}`}
                        className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm ${
                          m.sender_id === user?.user_id ? "brand-primary rounded-br-sm" : "bg-white border border-stone-200 text-stone-800 rounded-bl-sm"
                        }`}>
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-stone-200 flex items-center gap-2">
                  <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" />
                  <Button data-testid="chat-send" type="submit" className="brand-accent rounded-full"><Send className="w-4 h-4" /></Button>
                </form>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
